// Player Prop Markets Configuration
// Based on The Odds API documentation
// Each league has its own section with unique market keys (prefixed by league)

export interface PlayerPropMarket {
  key: string;        // Unique key (e.g., "nfl_player_pass_yds")
  apiKey: string;     // API key to send to backend (e.g., "player_pass_yds")
  name: string;
  isAlternate?: boolean;
}

export interface PlayerPropLeague {
  key: string;
  name: string;
  sportKey: string;   // Single API sport key
  markets: PlayerPropMarket[];
}

// Helper to create markets with league-prefixed keys
const createMarkets = (leagueKey: string, markets: Array<{ apiKey: string; name: string; isAlternate?: boolean }>): PlayerPropMarket[] => {
  return markets.map(m => ({
    key: `${leagueKey}_${m.apiKey}`,
    apiKey: m.apiKey,
    name: m.name,
    isAlternate: m.isAlternate,
  }));
};

// Football markets (shared by NFL, NCAAF, CFL)
const footballMarkets = [
  // Standard Markets
  { apiKey: 'player_pass_yds', name: 'Pass Yards' },
  { apiKey: 'player_pass_tds', name: 'Pass Touchdowns' },
  { apiKey: 'player_pass_attempts', name: 'Pass Attempts' },
  { apiKey: 'player_pass_completions', name: 'Pass Completions' },
  { apiKey: 'player_pass_interceptions', name: 'Pass Interceptions' },
  { apiKey: 'player_pass_longest_completion', name: 'Longest Pass Completion' },
  { apiKey: 'player_pass_yds_q1', name: '1st Quarter Pass Yards' },
  { apiKey: 'player_rush_yds', name: 'Rush Yards' },
  { apiKey: 'player_rush_tds', name: 'Rush Touchdowns' },
  { apiKey: 'player_rush_attempts', name: 'Rush Attempts' },
  { apiKey: 'player_rush_longest', name: 'Longest Rush' },
  { apiKey: 'player_reception_yds', name: 'Reception Yards' },
  { apiKey: 'player_reception_tds', name: 'Reception Touchdowns' },
  { apiKey: 'player_receptions', name: 'Receptions' },
  { apiKey: 'player_reception_longest', name: 'Longest Reception' },
  { apiKey: 'player_pass_rush_yds', name: 'Pass + Rush Yards' },
  { apiKey: 'player_rush_reception_yds', name: 'Rush + Reception Yards' },
  { apiKey: 'player_rush_reception_tds', name: 'Rush + Reception TDs' },
  { apiKey: 'player_pass_rush_reception_yds', name: 'Pass + Rush + Reception Yards' },
  { apiKey: 'player_pass_rush_reception_tds', name: 'Pass + Rush + Reception TDs' },
  { apiKey: 'player_anytime_td', name: 'Anytime Touchdown' },
  { apiKey: 'player_1st_td', name: 'First Touchdown' },
  { apiKey: 'player_last_td', name: 'Last Touchdown' },
  { apiKey: 'player_tds_over', name: 'Touchdowns (Over)' },
  { apiKey: 'player_field_goals', name: 'Field Goals' },
  { apiKey: 'player_kicking_points', name: 'Kicking Points' },
  { apiKey: 'player_pats', name: 'Points After Touchdown' },
  { apiKey: 'player_tackles_assists', name: 'Tackles + Assists' },
  { apiKey: 'player_solo_tackles', name: 'Solo Tackles' },
  { apiKey: 'player_sacks', name: 'Sacks' },
  { apiKey: 'player_defensive_interceptions', name: 'Defensive Interceptions' },
  { apiKey: 'player_assists', name: 'Assists' },
  // Alternate Markets
  { apiKey: 'player_pass_yds_alternate', name: 'Alt Pass Yards', isAlternate: true },
  { apiKey: 'player_pass_tds_alternate', name: 'Alt Pass Touchdowns', isAlternate: true },
  { apiKey: 'player_pass_attempts_alternate', name: 'Alt Pass Attempts', isAlternate: true },
  { apiKey: 'player_pass_completions_alternate', name: 'Alt Pass Completions', isAlternate: true },
  { apiKey: 'player_pass_interceptions_alternate', name: 'Alt Pass Interceptions', isAlternate: true },
  { apiKey: 'player_pass_longest_completion_alternate', name: 'Alt Longest Pass Completion', isAlternate: true },
  { apiKey: 'player_pass_rush_yds_alternate', name: 'Alt Pass + Rush Yards', isAlternate: true },
  { apiKey: 'player_rush_yds_alternate', name: 'Alt Rush Yards', isAlternate: true },
  { apiKey: 'player_rush_tds_alternate', name: 'Alt Rush Touchdowns', isAlternate: true },
  { apiKey: 'player_rush_attempts_alternate', name: 'Alt Rush Attempts', isAlternate: true },
  { apiKey: 'player_rush_longest_alternate', name: 'Alt Longest Rush', isAlternate: true },
  { apiKey: 'player_reception_yds_alternate', name: 'Alt Reception Yards', isAlternate: true },
  { apiKey: 'player_reception_tds_alternate', name: 'Alt Reception TDs', isAlternate: true },
  { apiKey: 'player_receptions_alternate', name: 'Alt Receptions', isAlternate: true },
  { apiKey: 'player_reception_longest_alternate', name: 'Alt Longest Reception', isAlternate: true },
  { apiKey: 'player_rush_reception_yds_alternate', name: 'Alt Rush + Reception Yards', isAlternate: true },
  { apiKey: 'player_rush_reception_tds_alternate', name: 'Alt Rush + Reception TDs', isAlternate: true },
  { apiKey: 'player_pass_rush_reception_yds_alternate', name: 'Alt Pass + Rush + Reception Yards', isAlternate: true },
  { apiKey: 'player_pass_rush_reception_tds_alternate', name: 'Alt Pass + Rush + Reception TDs', isAlternate: true },
  { apiKey: 'player_field_goals_alternate', name: 'Alt Field Goals', isAlternate: true },
  { apiKey: 'player_kicking_points_alternate', name: 'Alt Kicking Points', isAlternate: true },
  { apiKey: 'player_pats_alternate', name: 'Alt Points After Touchdown', isAlternate: true },
  { apiKey: 'player_tackles_assists_alternate', name: 'Alt Tackles + Assists', isAlternate: true },
  { apiKey: 'player_solo_tackles_alternate', name: 'Alt Solo Tackles', isAlternate: true },
  { apiKey: 'player_sacks_alternate', name: 'Alt Sacks', isAlternate: true },
  { apiKey: 'player_assists_alternate', name: 'Alt Assists', isAlternate: true },
];

// Basketball markets (shared by NBA, NCAAB, WNBA)
const basketballMarkets = [
  // Standard Markets
  { apiKey: 'player_points', name: 'Points' },
  { apiKey: 'player_points_q1', name: '1st Quarter Points' },
  { apiKey: 'player_rebounds', name: 'Rebounds' },
  { apiKey: 'player_rebounds_q1', name: '1st Quarter Rebounds' },
  { apiKey: 'player_assists', name: 'Assists' },
  { apiKey: 'player_assists_q1', name: '1st Quarter Assists' },
  { apiKey: 'player_threes', name: 'Three Pointers' },
  { apiKey: 'player_blocks', name: 'Blocks' },
  { apiKey: 'player_steals', name: 'Steals' },
  { apiKey: 'player_blocks_steals', name: 'Blocks + Steals' },
  { apiKey: 'player_turnovers', name: 'Turnovers' },
  { apiKey: 'player_points_rebounds_assists', name: 'Points + Rebounds + Assists' },
  { apiKey: 'player_points_rebounds', name: 'Points + Rebounds' },
  { apiKey: 'player_points_assists', name: 'Points + Assists' },
  { apiKey: 'player_rebounds_assists', name: 'Rebounds + Assists' },
  { apiKey: 'player_field_goals', name: 'Field Goals' },
  { apiKey: 'player_frees_made', name: 'Free Throws Made' },
  { apiKey: 'player_frees_attempts', name: 'Free Throws Attempted' },
  { apiKey: 'player_first_basket', name: 'First Basket Scorer' },
  { apiKey: 'player_first_team_basket', name: 'First Team Basket' },
  { apiKey: 'player_double_double', name: 'Double Double' },
  { apiKey: 'player_triple_double', name: 'Triple Double' },
  { apiKey: 'player_method_of_first_basket', name: 'Method of First Basket' },
  // Alternate Markets
  { apiKey: 'player_points_alternate', name: 'Alt Points', isAlternate: true },
  { apiKey: 'player_rebounds_alternate', name: 'Alt Rebounds', isAlternate: true },
  { apiKey: 'player_assists_alternate', name: 'Alt Assists', isAlternate: true },
  { apiKey: 'player_blocks_alternate', name: 'Alt Blocks', isAlternate: true },
  { apiKey: 'player_steals_alternate', name: 'Alt Steals', isAlternate: true },
  { apiKey: 'player_turnovers_alternate', name: 'Alt Turnovers', isAlternate: true },
  { apiKey: 'player_threes_alternate', name: 'Alt Three Pointers', isAlternate: true },
  { apiKey: 'player_points_assists_alternate', name: 'Alt Points + Assists', isAlternate: true },
  { apiKey: 'player_points_rebounds_alternate', name: 'Alt Points + Rebounds', isAlternate: true },
  { apiKey: 'player_rebounds_assists_alternate', name: 'Alt Rebounds + Assists', isAlternate: true },
  { apiKey: 'player_points_rebounds_assists_alternate', name: 'Alt Points + Rebounds + Assists', isAlternate: true },
];

// Soccer markets (shared by all soccer leagues)
const soccerMarkets = [
  { apiKey: 'player_goal_scorer_anytime', name: 'Anytime Goal Scorer' },
  { apiKey: 'player_first_goal_scorer', name: 'First Goal Scorer' },
  { apiKey: 'player_last_goal_scorer', name: 'Last Goal Scorer' },
  { apiKey: 'player_to_receive_card', name: 'Player To Receive Card' },
  { apiKey: 'player_to_receive_red_card', name: 'Player To Receive Red Card' },
  { apiKey: 'player_shots_on_target', name: 'Shots on Target' },
  { apiKey: 'player_shots', name: 'Shots' },
  { apiKey: 'player_assists', name: 'Assists' },
];

export const PLAYER_PROP_LEAGUES: PlayerPropLeague[] = [
  // American Football Leagues (separate)
  {
    key: 'nfl',
    name: 'NFL',
    sportKey: 'americanfootball_nfl',
    markets: createMarkets('nfl', footballMarkets),
  },
  {
    key: 'ncaaf',
    name: 'NCAAF',
    sportKey: 'americanfootball_ncaaf',
    markets: createMarkets('ncaaf', footballMarkets),
  },
  {
    key: 'cfl',
    name: 'CFL',
    sportKey: 'americanfootball_cfl',
    markets: createMarkets('cfl', footballMarkets),
  },
  // Basketball Leagues (separate)
  {
    key: 'nba',
    name: 'NBA',
    sportKey: 'basketball_nba',
    markets: createMarkets('nba', basketballMarkets),
  },
  {
    key: 'ncaab',
    name: 'NCAAB',
    sportKey: 'basketball_ncaab',
    markets: createMarkets('ncaab', basketballMarkets),
  },
  {
    key: 'wnba',
    name: 'WNBA',
    sportKey: 'basketball_wnba',
    markets: createMarkets('wnba', basketballMarkets),
  },
  // MLB
  {
    key: 'mlb',
    name: 'MLB',
    sportKey: 'baseball_mlb',
    markets: createMarkets('mlb', [
      // Standard Markets
      { apiKey: 'batter_home_runs', name: 'Batter Home Runs' },
      { apiKey: 'batter_first_home_run', name: 'Batter First Home Run' },
      { apiKey: 'batter_hits', name: 'Batter Hits' },
      { apiKey: 'batter_total_bases', name: 'Batter Total Bases' },
      { apiKey: 'batter_rbis', name: 'Batter RBIs' },
      { apiKey: 'batter_runs_scored', name: 'Batter Runs Scored' },
      { apiKey: 'batter_hits_runs_rbis', name: 'Batter Hits + Runs + RBIs' },
      { apiKey: 'batter_singles', name: 'Batter Singles' },
      { apiKey: 'batter_doubles', name: 'Batter Doubles' },
      { apiKey: 'batter_triples', name: 'Batter Triples' },
      { apiKey: 'batter_walks', name: 'Batter Walks' },
      { apiKey: 'batter_strikeouts', name: 'Batter Strikeouts' },
      { apiKey: 'batter_stolen_bases', name: 'Batter Stolen Bases' },
      { apiKey: 'pitcher_strikeouts', name: 'Pitcher Strikeouts' },
      { apiKey: 'pitcher_record_a_win', name: 'Pitcher To Record Win' },
      { apiKey: 'pitcher_hits_allowed', name: 'Pitcher Hits Allowed' },
      { apiKey: 'pitcher_walks', name: 'Pitcher Walks' },
      { apiKey: 'pitcher_earned_runs', name: 'Pitcher Earned Runs' },
      { apiKey: 'pitcher_outs', name: 'Pitcher Outs' },
      // Alternate Markets
      { apiKey: 'batter_total_bases_alternate', name: 'Alt Batter Total Bases', isAlternate: true },
      { apiKey: 'batter_home_runs_alternate', name: 'Alt Batter Home Runs', isAlternate: true },
      { apiKey: 'batter_hits_alternate', name: 'Alt Batter Hits', isAlternate: true },
      { apiKey: 'batter_rbis_alternate', name: 'Alt Batter RBIs', isAlternate: true },
      { apiKey: 'batter_walks_alternate', name: 'Alt Batter Walks', isAlternate: true },
      { apiKey: 'batter_strikeouts_alternate', name: 'Alt Batter Strikeouts', isAlternate: true },
      { apiKey: 'batter_runs_scored_alternate', name: 'Alt Batter Runs Scored', isAlternate: true },
      { apiKey: 'batter_singles_alternate', name: 'Alt Batter Singles', isAlternate: true },
      { apiKey: 'batter_doubles_alternate', name: 'Alt Batter Doubles', isAlternate: true },
      { apiKey: 'batter_triples_alternate', name: 'Alt Batter Triples', isAlternate: true },
      { apiKey: 'pitcher_hits_allowed_alternate', name: 'Alt Pitcher Hits Allowed', isAlternate: true },
      { apiKey: 'pitcher_walks_alternate', name: 'Alt Pitcher Walks', isAlternate: true },
      { apiKey: 'pitcher_strikeouts_alternate', name: 'Alt Pitcher Strikeouts', isAlternate: true },
    ]),
  },
  // NHL
  {
    key: 'nhl',
    name: 'NHL',
    sportKey: 'icehockey_nhl',
    markets: createMarkets('nhl', [
      // Standard Markets
      { apiKey: 'player_points', name: 'Points' },
      { apiKey: 'player_power_play_points', name: 'Power Play Points' },
      { apiKey: 'player_assists', name: 'Assists' },
      { apiKey: 'player_blocked_shots', name: 'Blocked Shots' },
      { apiKey: 'player_shots_on_goal', name: 'Shots on Goal' },
      { apiKey: 'player_goals', name: 'Goals' },
      { apiKey: 'player_total_saves', name: 'Total Saves' },
      { apiKey: 'player_goal_scorer_first', name: 'First Goal Scorer' },
      { apiKey: 'player_goal_scorer_last', name: 'Last Goal Scorer' },
      { apiKey: 'player_goal_scorer_anytime', name: 'Anytime Goal Scorer' },
      // Alternate Markets
      { apiKey: 'player_points_alternate', name: 'Alt Points', isAlternate: true },
      { apiKey: 'player_assists_alternate', name: 'Alt Assists', isAlternate: true },
      { apiKey: 'player_power_play_points_alternate', name: 'Alt Power Play Points', isAlternate: true },
      { apiKey: 'player_goals_alternate', name: 'Alt Goals', isAlternate: true },
      { apiKey: 'player_shots_on_goal_alternate', name: 'Alt Shots on Goal', isAlternate: true },
      { apiKey: 'player_blocked_shots_alternate', name: 'Alt Blocked Shots', isAlternate: true },
      { apiKey: 'player_total_saves_alternate', name: 'Alt Total Saves', isAlternate: true },
    ]),
  },
  // AFL
  {
    key: 'afl',
    name: 'AFL',
    sportKey: 'aussierules_afl',
    markets: createMarkets('afl', [
      { apiKey: 'player_disposals', name: 'Disposals' },
      { apiKey: 'player_disposals_over', name: 'Disposals (Over)' },
      { apiKey: 'player_goal_scorer_first', name: 'First Goal Scorer' },
      { apiKey: 'player_goal_scorer_last', name: 'Last Goal Scorer' },
      { apiKey: 'player_goal_scorer_anytime', name: 'Anytime Goal Scorer' },
      { apiKey: 'player_goals_scored_over', name: 'Goals Scored (Over)' },
      { apiKey: 'player_marks_over', name: 'Marks (Over)' },
      { apiKey: 'player_marks_most', name: 'Most Marks' },
      { apiKey: 'player_tackles_over', name: 'Tackles (Over)' },
      { apiKey: 'player_tackles_most', name: 'Most Tackles' },
      { apiKey: 'player_afl_fantasy_points', name: 'AFL Fantasy Points' },
      { apiKey: 'player_afl_fantasy_points_over', name: 'AFL Fantasy Points (Over)' },
      { apiKey: 'player_afl_fantasy_points_most', name: 'Most AFL Fantasy Points' },
    ]),
  },
  // NRL
  {
    key: 'nrl',
    name: 'NRL',
    sportKey: 'rugbyleague_nrl',
    markets: createMarkets('nrl', [
      { apiKey: 'player_try_scorer_first', name: 'First Try Scorer' },
      { apiKey: 'player_try_scorer_last', name: 'Last Try Scorer' },
      { apiKey: 'player_try_scorer_anytime', name: 'Anytime Try Scorer' },
      { apiKey: 'player_try_scorer_over', name: 'Tries Scored (Over)' },
    ]),
  },
  // Soccer Leagues (separate)
  {
    key: 'epl',
    name: 'EPL',
    sportKey: 'soccer_epl',
    markets: createMarkets('epl', soccerMarkets),
  },
  {
    key: 'ligue1',
    name: 'Ligue 1',
    sportKey: 'soccer_france_ligue_one',
    markets: createMarkets('ligue1', soccerMarkets),
  },
  {
    key: 'bundesliga',
    name: 'Bundesliga',
    sportKey: 'soccer_germany_bundesliga',
    markets: createMarkets('bundesliga', soccerMarkets),
  },
  {
    key: 'seriea',
    name: 'Serie A',
    sportKey: 'soccer_italy_serie_a',
    markets: createMarkets('seriea', soccerMarkets),
  },
  {
    key: 'laliga',
    name: 'La Liga',
    sportKey: 'soccer_spain_la_liga',
    markets: createMarkets('laliga', soccerMarkets),
  },
  {
    key: 'mls',
    name: 'MLS',
    sportKey: 'soccer_usa_mls',
    markets: createMarkets('mls', soccerMarkets),
  },
];

// Maximum number of player prop markets that can be selected at once
export const MAX_PLAYER_PROP_MARKETS = 10;

// Top 5 player prop markets per sport for automatic selection (Platinum users)
// These are the most popular/liquid markets for each sport
export const TOP_PLAYER_PROP_MARKETS_BY_SPORT: Record<string, string[]> = {
  // American Football (NFL, NCAAF, CFL)
  'americanfootball_nfl': ['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_receptions', 'player_anytime_td'],
  'americanfootball_ncaaf': ['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_receptions', 'player_anytime_td'],
  'americanfootball_cfl': ['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_receptions', 'player_anytime_td'],

  // Basketball (NBA, NCAAB, WNBA)
  'basketball_nba': ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists'],
  'basketball_ncaab': ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists'],
  'basketball_wnba': ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists'],

  // Baseball (MLB)
  'baseball_mlb': ['batter_hits', 'pitcher_strikeouts', 'batter_total_bases', 'batter_home_runs', 'batter_rbis'],

  // Ice Hockey (NHL)
  'icehockey_nhl': ['player_points', 'player_goals', 'player_assists', 'player_shots_on_goal', 'player_total_saves'],

  // Soccer (all leagues)
  'soccer_epl': ['player_goal_scorer_anytime', 'player_shots_on_target', 'player_shots', 'player_assists', 'player_first_goal_scorer'],
  'soccer_france_ligue_one': ['player_goal_scorer_anytime', 'player_shots_on_target', 'player_shots', 'player_assists', 'player_first_goal_scorer'],
  'soccer_germany_bundesliga': ['player_goal_scorer_anytime', 'player_shots_on_target', 'player_shots', 'player_assists', 'player_first_goal_scorer'],
  'soccer_italy_serie_a': ['player_goal_scorer_anytime', 'player_shots_on_target', 'player_shots', 'player_assists', 'player_first_goal_scorer'],
  'soccer_spain_la_liga': ['player_goal_scorer_anytime', 'player_shots_on_target', 'player_shots', 'player_assists', 'player_first_goal_scorer'],
  'soccer_usa_mls': ['player_goal_scorer_anytime', 'player_shots_on_target', 'player_shots', 'player_assists', 'player_first_goal_scorer'],

  // Australian Rules (AFL)
  'aussierules_afl': ['player_disposals', 'player_goal_scorer_anytime', 'player_afl_fantasy_points', 'player_marks_over', 'player_tackles_over'],

  // Rugby League (NRL) - only 4 markets available
  'rugbyleague_nrl': ['player_try_scorer_anytime', 'player_try_scorer_first', 'player_try_scorer_last', 'player_try_scorer_over'],
};

// Helper to get top player prop markets for a given sport key
export const getTopPlayerPropMarketsForSport = (sportKey: string): string[] => {
  return TOP_PLAYER_PROP_MARKETS_BY_SPORT[sportKey] || [];
};

// Helper function to get total market count
export const getTotalMarketCount = (): number => {
  return PLAYER_PROP_LEAGUES.reduce((sum, league) => sum + league.markets.length, 0);
};

// Helper function to get league by key
export const getLeagueByKey = (key: string): PlayerPropLeague | undefined => {
  return PLAYER_PROP_LEAGUES.find(league => league.key === key);
};

// Helper function to get all market keys for a specific league
export const getMarketKeysForLeague = (leagueKey: string): string[] => {
  const league = getLeagueByKey(leagueKey);
  return league ? league.markets.map(m => m.key) : [];
};

// Helper to format market selections for display
export const formatSelectedMarkets = (selectedMarkets: string[]): string => {
  if (selectedMarkets.length === 0) return 'None selected';
  if (selectedMarkets.length === 1) return '1 market';
  return `${selectedMarkets.length} markets`;
};

// Helper to parse a market key and get its league and API key
export const parseMarketKey = (marketKey: string): { leagueKey: string; apiKey: string } | null => {
  for (const league of PLAYER_PROP_LEAGUES) {
    const market = league.markets.find(m => m.key === marketKey);
    if (market) {
      return { leagueKey: league.key, apiKey: market.apiKey };
    }
  }
  return null;
};

// Helper to get selected markets grouped by league (for API calls)
export const getSelectedMarketsByLeague = (selectedMarkets: string[]): Map<string, string[]> => {
  const result = new Map<string, string[]>();

  for (const marketKey of selectedMarkets) {
    for (const league of PLAYER_PROP_LEAGUES) {
      const market = league.markets.find(m => m.key === marketKey);
      if (market) {
        const existing = result.get(league.sportKey) || [];
        existing.push(market.apiKey);
        result.set(league.sportKey, existing);
        break;
      }
    }
  }

  return result;
};
